/**
 * Renders a ticket body or comment as Markdown.
 *
 * Ticket text used to render as a plain string in a `white-space: pre-wrap`
 * span. People were already writing lists, code and links into it by hand; this
 * just stops throwing that away — and it is what makes a pasted screenshot show
 * up, since an attachment is referenced as ordinary Markdown image syntax.
 *
 * **No raw HTML.** `react-markdown` escapes embedded HTML unless `rehype-raw` is
 * added, and it is deliberately not added here. Ticket bodies are untrusted text
 * on a board every signed-in user can read, so the one thing this must not do is
 * turn a report into a way to run markup in someone else's session. Markdown
 * covers everything the feature was asked for.
 *
 * Same renderer for bodies and comments on purpose: two of these would drift,
 * and the drift would be silent because both render "fine" either way.
 */
import { useEffect, useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { makeStyles, shorthands, tokens } from '@fluentui/react-components'
import api from '../lib/api'
import { blobErrorMessage } from '../lib/apiError'

/**
 * One of OUR attachment URLs — and nothing else.
 *
 * Anchored at `/` so it can only ever match a root-relative path. That anchor is
 * the security half of this pattern: the api client attaches our bearer token to
 * every request it makes, so something loose enough to also match
 * `https://evil.example/tickets/attachments/1` would hand that token to whoever
 * a reporter linked. Ticket text is untrusted, and an image URL inside it is
 * attacker-chosen.
 *
 * The `/api` segment is optional because it is `settings.API_PREFIX` server
 * side, and a stored ticket body outlives any change to it.
 */
const ATTACHMENT_URL = /^\/(?:api\/)?tickets\/attachments\/(\d+)$/

/**
 * An attachment rendered from bytes we fetched ourselves.
 *
 * `GET /tickets/attachments/{id}` requires a bearer token. An `<img src>` is a
 * plain browser request and carries no Authorization header — so from v2.8.91
 * until this component existed, every pasted screenshot resolved to a 401 and a
 * broken-image icon. The feature had never once worked in a browser, and the
 * test covering it asserted the `src` attribute rather than that anything
 * loaded, so it passed the whole time.
 *
 * The bytes therefore come through the api client, which does carry the token,
 * and the element points at an object URL instead.
 */
function AttachmentImage({ src, alt }: { src?: string; alt?: string }) {
  const id = (typeof src === 'string' ? src.match(ATTACHMENT_URL)?.[1] : undefined) ?? null
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    if (id === null) return
    let cancelled = false
    let created: string | null = null

    api
      .get(`/tickets/attachments/${id}`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return
        created = URL.createObjectURL(res.data as Blob)
        setObjectUrl(created)
      })
      .catch(async (err: unknown) => {
        // `responseType: 'blob'` applies to the ERROR body too, so the server's
        // explanation arrives as a Blob and `apiErrorMessage` cannot see into
        // it. Without this, a deleted attachment, an expired session and a
        // server fault all render the same sentence.
        const reason = await blobErrorMessage(err, '')
        if (!cancelled) setFailure(reason)
      })

    // Revoking on unmount matters more here than in most places: a ticket
    // thread can carry a dozen screenshots and the board is left open.
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [id])

  // Not ours — rendered as-is, exactly as before, and deliberately NOT through
  // the api client. See the comment on ATTACHMENT_URL.
  if (id === null) {
    const href = typeof src === 'string' ? src : undefined
    return (
      <a href={href} target="_blank" rel="noreferrer">
        <img src={href} alt={alt ?? ''} />
      </a>
    )
  }

  if (failure !== null) {
    // Naming the file beats a broken-image icon, which reads as "the app is
    // broken" rather than "this one attachment is missing".
    return (
      <span data-testid="attachment-failed">
        {alt || 'This image'} could not be loaded.{failure ? ` ${failure}` : ''}
      </span>
    )
  }

  if (!objectUrl) return null

  // Open at full size. A 480px-tall screenshot of a bug is often unreadable, and
  // the alternative — a lightbox — is a lot of component for "let me see it
  // properly". An object URL opens in a new tab the same way a path would.
  return (
    <a href={objectUrl} target="_blank" rel="noreferrer">
      <img src={objectUrl} alt={alt ?? ''} />
    </a>
  )
}

const useStyles = makeStyles({
  root: {
    // Markdown emits block elements; without this the first and last of them
    // add margins the surrounding card did not ask for.
    '& > *:first-child': { marginTop: 0 },
    '& > *:last-child': { marginBottom: 0 },
    '& p': { marginTop: 0, marginBottom: tokens.spacingVerticalS },
    '& ul, & ol': { marginTop: 0, marginBottom: tokens.spacingVerticalS, paddingLeft: '1.4em' },
    '& code': {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase200,
      backgroundColor: tokens.colorNeutralBackground3,
      ...shorthands.padding('1px', '4px'),
      borderRadius: tokens.borderRadiusSmall,
    },
    '& pre': {
      backgroundColor: tokens.colorNeutralBackground3,
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
      borderRadius: tokens.borderRadiusMedium,
      // A stack trace must not widen the card and push the page sideways.
      overflowX: 'auto',
    },
    '& pre code': { backgroundColor: 'transparent', ...shorthands.padding(0) },
    '& blockquote': {
      marginLeft: 0,
      paddingLeft: tokens.spacingHorizontalM,
      borderLeft: `3px solid ${tokens.colorNeutralStroke2}`,
      color: tokens.colorNeutralForeground2,
    },
    '& table': { borderCollapse: 'collapse', display: 'block', overflowX: 'auto' },
    '& th, & td': {
      ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
      ...shorthands.padding('2px', '8px'),
    },
    // Screenshots are the point of the feature and they arrive at whatever size
    // the reporter's display happens to be, so they are bounded here rather than
    // trusted to be reasonable.
    '& img': {
      maxWidth: '100%',
      maxHeight: '480px',
      height: 'auto',
      display: 'block',
      borderRadius: tokens.borderRadiusMedium,
      ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
      marginTop: tokens.spacingVerticalXS,
      marginBottom: tokens.spacingVerticalXS,
      cursor: 'zoom-in',
    },
    '& [data-testid="attachment-failed"]': {
      display: 'block',
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground3,
      fontStyle: 'italic',
    },
  },
})

export function TicketMarkdown({ children }: { children: string }) {
  const styles = useStyles()
  return (
    <div className={styles.root}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          img: ({ src, alt }) => (
            <AttachmentImage src={typeof src === 'string' ? src : undefined} alt={alt} />
          ),
          // Ticket text is untrusted and can contain arbitrary links, so every
          // one of them gets noopener via `noreferrer` — without it the opened
          // page can navigate this tab through `window.opener`.
          a: ({ href, children: linkChildren }) => (
            <a href={href} target="_blank" rel="noreferrer">
              {linkChildren}
            </a>
          ),
        }}
      >
        {children}
      </Markdown>
    </div>
  )
}
